(() => {
  // ✅ sadece koleksiyon sayfasında çalışsın
  const isCollection = () =>
    document.body.classList.contains("template-collection") ||
    document.querySelector("[data-burn-scope]");

  // -----------------------------
  // Utils
  // -----------------------------
  const TRANSITION_MS = 800; // CSS --burn-transition ile uyumlu
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  function findMediaWrap(img) {
    return (
      img.closest(".media, .card__media, .product-card-image, .card__inner") ||
      img.parentElement
    );
  }

  function markReady(onImg) {
    onImg.dataset.burnReady = "1";
  }

  function isReady(onImg) {
    return onImg && onImg.dataset.burnReady === "1";
  }

  // -----------------------------
  // Dual-layer (crossfade) sistemi
  // - OFF: mevcut img
  // - ON : clone img (data-on-src)
  // - Önemli: ON yüklenmeden görünür yapmıyoruz
  // -----------------------------
  function ensureDualLayer(img) {
    const onSrc = img.getAttribute("data-on-src");
    if (!onSrc) return false;

    const wrap = findMediaWrap(img);
    if (!wrap) return false;

    // Wrapper’ı layer container'a çevir
    wrap.classList.add("burn-layer-wrap");
    if (getComputedStyle(wrap).position === "static") {
      wrap.style.position = "relative";
    }

    // OFF layer: mevcut img
    img.classList.add("burn-off");

    // Zaten ON layer varsa tekrar üretme
    let onImg = wrap.querySelector("img.burn-on");
    if (onImg) return true;

    onImg = img.cloneNode(true);
    onImg.classList.remove("burn-off");
    onImg.classList.add("burn-on");
    onImg.setAttribute("aria-hidden", "true");

    const onSrcset = img.getAttribute("data-on-srcset");
    if (onSrcset) onImg.setAttribute("srcset", onSrcset);
    onImg.setAttribute("src", onSrc);

    // Lazy yüzünden “tık sonrası” geç gelmesin:
    // - eager: mümkünse hemen alsın
    // - decoding async: paint’i bloklamasın
    onImg.setAttribute("loading", "eager");
    onImg.setAttribute("decoding", "async");

    // Başlangıçta READY değil (CSS: burn-mode olsa bile görünmesin)
    onImg.dataset.burnReady = "0";

    // Load olduğunda ready işaretle
    onImg.addEventListener(
      "load",
      () => {
        if (onImg.naturalWidth > 0) markReady(onImg);
      },
      { once: true }
    );
    onImg.addEventListener(
      "error",
      () => {
        // hata olursa hiç hazır sayma, OFF kalsın
        onImg.dataset.burnReady = "0";
      },
      { once: true }
    );

    // DOM'a ekle
    wrap.appendChild(onImg);

    // Eğer cache’te hazır gelirse:
    if (onImg.complete && onImg.naturalWidth > 0) markReady(onImg);

    // decode varsa (hızlandırır ama zorunlu değil)
    if (onImg.decode) onImg.decode().catch(() => {});

    return true;
  }

  // ON layer hazırsa crossfade çalışsın; değilse OFF kalsın
  function applyDualLayerVisibility(img, burnOn) {
    const wrap = findMediaWrap(img);
    if (!wrap) return;

    const onImg = wrap.querySelector("img.burn-on");
    const offImg = wrap.querySelector("img.burn-off") || img;

    if (!onImg) return;

    // ON hazır değilse: her durumda OFF görünür kalsın
    if (!isReady(onImg)) {
      offImg.style.opacity = "1";
      onImg.style.opacity = "0";
      return;
    }

    // Hazırsa: toggle’a göre crossfade
    if (burnOn) {
      offImg.style.opacity = "0";
      onImg.style.opacity = "1";
    } else {
      offImg.style.opacity = "1";
      onImg.style.opacity = "0";
    }
  }

  // -----------------------------
  // Fallback swap (data-on-src yoksa)
  // -----------------------------
  function getTargetSrc(img, on) {
    const offSrc = img.getAttribute("data-off-src");
    const offSrcset = img.getAttribute("data-off-srcset");
    const onSrc = img.getAttribute("data-on-src");
    const onSrcset = img.getAttribute("data-on-srcset");

    if (on && !onSrc) return null;
    return on ? { src: onSrc, srcset: onSrcset } : { src: offSrc, srcset: offSrcset };
  }

  async function waitImageReady(img) {
    if (img.complete && img.naturalWidth > 0) {
      if (img.decode) {
        try { await img.decode(); } catch (_) {}
      }
      return;
    }

    await new Promise((res) => {
      const done = () => res();
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });

    if (img.decode) {
      try { await img.decode(); } catch (_) {}
    }
  }

  async function swapImage(img, on) {
    const target = getTargetSrc(img, on);
    if (!target) return;

    img.style.opacity = "0";
    await sleep(120);

    if (target.srcset) img.setAttribute("srcset", target.srcset);
    if (target.src) img.setAttribute("src", target.src);

    await waitImageReady(img);

    requestAnimationFrame(() => (img.style.opacity = "1"));
  }

  // -----------------------------
  // Preload: sadece görünür/near-viewport ON görsellerini önceden al
  // -----------------------------
  function preloadVisibleOnImages() {
    const imgs = document.querySelectorAll("img[data-off-src][data-on-src]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const img = e.target;
          ensureDualLayer(img); // bu ON'u DOM’a ekleyip network request’i başlatır
          io.unobserve(img);
        });
      },
      { root: null, rootMargin: "600px 0px", threshold: 0.01 } // 600px önceden
    );

    imgs.forEach((img) => io.observe(img));
  }

  // -----------------------------
  // Mode
  // -----------------------------
  function setMode(on) {
    if (!isCollection()) on = false;

    document.body.classList.toggle("burn-mode", on);

    const imgs = document.querySelectorAll("img[data-off-src]");
    imgs.forEach((img) => {
      const ok = ensureDualLayer(img);
      if (ok) {
        applyDualLayerVisibility(img, on);

        // Eğer ON hazır değilse, hazır olur olmaz burn ON ise anında crossfade yap
        const wrap = findMediaWrap(img);
        const onImg = wrap && wrap.querySelector("img.burn-on");
        if (on && onImg && !isReady(onImg)) {
          onImg.addEventListener(
            "load",
            () => applyDualLayerVisibility(img, true),
            { once: true }
          );
        }
      } else {
        // data-on-src yoksa fallback swap
        swapImage(img, on);
      }
    });
  }

  function init() {
    if (!isCollection()) {
      document.body.classList.remove("burn-mode");
      return;
    }

    const btn = document.querySelector("[data-burn-toggle]");
    if (!btn) return;

    const saved = false; // ✅ her load OFF

    // Görünür ürünleri preload et (gecikmeyi ciddi azaltır)
    preloadVisibleOnImages();

    // ilk yüklemede transition patlamasın
    document.documentElement.style.setProperty("--burn-init", "1");
    setMode(saved);
    setTimeout(() => {
      document.documentElement.style.removeProperty("--burn-init");
    }, 50);

    btn.setAttribute("aria-pressed", saved ? "true" : "false");

    btn.addEventListener("click", () => {
      const next = !document.body.classList.contains("burn-mode");
      setMode(next);
      btn.setAttribute("aria-pressed", next ? "true" : "false");
    });
  }

  document.addEventListener("DOMContentLoaded", init);

  window.addEventListener("pageshow", () => {
    if (!isCollection()) document.body.classList.remove("burn-mode");
  });
})();
